/**
 * /accounting/ap/vendor-statements — Vendor statement reconciliation.
 *
 * Pick a vendor → paste their statement (date,description,amount[,ref]) →
 * each unmatched statement line gets ranked candidate bills (amount + date +
 * invoice memo). One-click attest match; no GL post needed (bill JE already exists).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { useAP } from "@/data/accounting/ap/APContext";
import { useVendorPO } from "@/data/VendorPOContext";

import { BooksNavRail } from "@/components/accounting/BooksNavRail";
const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const dayMs = 24 * 60 * 60 * 1000;
const CENT = (n: number) => Math.round(n * 100);

export default function VendorStatementsPage() {
  const { vendors } = useVendorPO();
  const { bills, statementLines, importVendorStatement,
    matchStatementLineToBill, ignoreStatementLine, getOpenBalanceForVendor } = useAP();
  const [vendorId, setVendorId] = useState<string>("");
  const [csv, setCsv] = useState("");

  const scopedLines = useMemo(
    () => statementLines.filter(l => !vendorId || l.vendorId === vendorId),
    [statementLines, vendorId],
  );
  const vendorBills = useMemo(
    () => bills.filter(b => b.vendorId === vendorId && b.status !== "void"),
    [bills, vendorId],
  );

  const suggestionsFor = (lineId: string) => {
    const line = scopedLines.find(l => l.id === lineId);
    if (!line) return [];
    return vendorBills
      .map(b => {
        const dDays = Math.abs(new Date(b.billDate).getTime() - new Date(line.postedDate).getTime()) / dayMs;
        let score = 0;
        const reasons: string[] = [];
        if (CENT(b.total) === CENT(line.amount)) { score += 60; reasons.push("Amount matches"); }
        else if (CENT(b.total - b.amountPaid) === CENT(line.amount)) { score += 50; reasons.push("Open balance matches"); }
        if (line.description.toLowerCase().includes(b.invoiceNumber.toLowerCase())) {
          score += 25; reasons.push("Invoice # in memo");
        }
        if (dDays <= 3) { score += 15; reasons.push("Close dates"); }
        else if (dDays <= 14) { score += 5; }
        return { bill: b, score, reasons };
      })
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  };

  const handleImport = () => {
    if (!vendorId) return toast({ title: "Pick a vendor first", variant: "destructive" });
    if (!csv.trim()) return;
    const { imported, errors } = importVendorStatement(vendorId, csv);
    toast({
      title: `Imported ${imported} line${imported === 1 ? "" : "s"}`,
      description: errors.length ? `${errors.length} skipped.` : "Ready to reconcile.",
    });
    if (imported > 0) setCsv("");
  };

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/accounting/ap"><ArrowLeft className="h-4 w-4 mr-1" /> Bills</Link>
        </Button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Vendor Statement Reconciliation</h1>
          <p className="text-xs text-muted-foreground">
            Compare what the vendor says you owe vs what's posted to AP.
          </p>
        </div>
      </div>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card p-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs text-muted-foreground">Vendor</label>
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
              <SelectContent>
                {vendors.filter(v => v.active !== false).map(v => (
                  <SelectItem key={v.vendorId} value={v.vendorId}>{v.vendorName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 text-sm text-muted-foreground">
            {vendorId && (
              <>
                Open AP balance:{" "}
                <span className="font-semibold text-foreground tabular-nums">
                  {fmt(getOpenBalanceForVendor(vendorId))}
                </span>
                {" · "}{vendorBills.length} bills on file
              </>
            )}
          </div>
        </div>
      </CockpitSection>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card p-3">
          <div className="flex items-center gap-2 mb-2">
            <Upload className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Import statement</span>
            <span className="text-xs text-muted-foreground">date, description, amount[, reference]</span>
          </div>
          <Textarea value={csv} onChange={e => setCsv(e.target.value)}
            placeholder={`2026-06-10, INV-44512 Materials,  3210.00, \n2026-06-22, INV-44612 Materials,  1850.00, `}
            className="font-mono text-xs h-24" />
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={handleImport} disabled={!csv.trim() || !vendorId}>Import</Button>
          </div>
        </div>
      </CockpitSection>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card">
          <div className="p-3 border-b border-border text-sm font-medium">
            Statement lines {vendorId ? `(${scopedLines.length})` : "(pick a vendor)"}
          </div>
          <div className="divide-y divide-border">
            {scopedLines.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Nothing imported yet for this vendor.
              </div>
            )}
            {scopedLines.map(l => {
              const sug = l.status === "unmatched" ? suggestionsFor(l.id) : [];
              return (
                <div key={l.id} className="p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{l.description}</div>
                      <div className="text-xs text-muted-foreground">
                        {fmtDate(l.postedDate)} {l.reference ? `· ${l.reference}` : ""}
                      </div>
                    </div>
                    <div className="text-sm font-semibold tabular-nums">{fmt(l.amount)}</div>
                    <Badge
                      variant={l.status === "matched" ? "secondary" :
                        l.status === "ignored" ? "outline" : "destructive"}
                      className="h-4 px-1 text-[10px] uppercase"
                    >
                      {l.status}
                    </Badge>
                  </div>
                  {l.status === "unmatched" && (
                    <div className="mt-2 pl-3 border-l-2 border-border space-y-1">
                      {sug.length === 0 ? (
                        <div className="text-xs text-muted-foreground">No candidate bills.</div>
                      ) : sug.map(s => (
                        <div key={s.bill.id} className="flex items-center justify-between gap-2 text-xs">
                          <div className="min-w-0">
                            <span className="font-medium">{s.bill.invoiceNumber}</span>
                            <span className="text-muted-foreground"> · {fmtDate(s.bill.billDate)} · {fmt(s.bill.total)}</span>
                            <span className="text-muted-foreground"> · {s.reasons.join(", ")}</span>
                          </div>
                          <Button size="sm" className="h-6 px-2"
                            onClick={() => {
                              matchStatementLineToBill(l.id, s.bill.id);
                              toast({ title: "Matched to bill" });
                            }}>
                            <Check className="h-3 w-3 mr-1" /> {s.score}
                          </Button>
                        </div>
                      ))}
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                        onClick={() => ignoreStatementLine(l.id)}>
                        <X className="h-3 w-3 mr-1" /> Ignore
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}
